// M05 MFA einrichten: Schritt 1 QR-Code + manueller Schlüssel, Schritt 2 Code bestätigen, Schritt 3 zehn
// Wiederherstellungscodes mit „Herunterladen“, „Drucken“ und Pflicht-Häkchen „Ich habe die Codes gespeichert“.
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { confirmTotp, serverMessage, startTotpSetup, type TotpSetup } from '../../api/account';
import type { Me } from '../../api/types';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/toast';
import { Field, Spinner } from '../../components/ui';
import '../../styles/account.css';

/** QR-Code als Inline-SVG (die CSP erlaubt keine data:-Bilder). */
export function QrImage({ qr, label }: { qr: TotpSetup['qr']; label: string }) {
  return (
    <svg className="qr" viewBox={`0 0 ${qr.dim} ${qr.dim}`} role="img" aria-label={label} shapeRendering="crispEdges" data-testid="mfa-qr">
      <rect width="100%" height="100%" fill="#fff" />
      <path d={qr.path} fill="#000" />
    </svg>
  );
}

/** Sechsstelliger Code: Ziffernfeld, Autofokus, Handy-Tastatur numerisch (M02). */
export function CodeInput({ value, onChange, label, autoFocus = true }: { value: string; onChange: (v: string) => void; label: string; autoFocus?: boolean }) {
  return (
    <Field label={label}>
      <input className="code-input mono" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]*" maxLength={7} autoFocus={autoFocus}
        value={value} onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))} placeholder="123456" />
    </Field>
  );
}

export function downloadCodes(codes: string[], email: string, t: (k: string, o?: Record<string, unknown>) => string) {
  const text = `${t('account.codes.fileTitle', { email })}\n${new Date().toLocaleString()}\n\n${codes.join('\n')}\n\n${t('account.codes.fileHint')}\n`;
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'disploraq-wiederherstellungscodes.txt';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function printCodes(codes: string[], email: string, t: (k: string, o?: Record<string, unknown>) => string) {
  const w = window.open('', '_blank', 'width=600,height=700');
  if (!w) return;
  const doc = w.document;
  doc.title = 'DisploraQ';
  const h = doc.createElement('h2');
  h.textContent = t('account.codes.fileTitle', { email });
  const pre = doc.createElement('pre');
  pre.style.fontSize = '18px';
  pre.textContent = codes.join('\n');
  const p = doc.createElement('p');
  p.textContent = t('account.codes.fileHint');
  doc.body.append(h, pre, p);
  w.focus();
  w.print();
}

/** Anzeige der Codes (Schritt 3 bzw. nach „Neue Codes“). */
export function RecoveryCodes({ codes, email, onDone, doneLabel }: { codes: string[]; email: string; onDone: () => void; doneLabel?: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [saved, setSaved] = useState(false);
  return (
    <div className="col">
      <div className="hint warn" role="note">{t('account.codes.once')}</div>
      <ol className="codes mono" aria-label={t('account.codes.title')} data-testid="recovery-codes">
        {codes.map((c) => <li key={c}>{c}</li>)}
      </ol>
      <div className="row">
        <button type="button" className="btn" onClick={() => downloadCodes(codes, email, t)}><Icon name="download" />{t('account.codes.download')}</button>
        <button type="button" className="btn" onClick={() => printCodes(codes, email, t)}><Icon name="copy" />{t('account.codes.print')}</button>
        <button type="button" className="btn ghost" onClick={() => void navigator.clipboard?.writeText(codes.join('\n')).then(() => toast.show(t('common.copied')))}>
          <Icon name="copy" />{t('common.copy')}
        </button>
      </div>
      <label className="check"><input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />{t('account.codes.saved')}</label>
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn primary" disabled={!saved} onClick={onDone}>{doneLabel ?? t('account.mfa.finish')}</button>
      </div>
    </div>
  );
}

/**
 * Assistent in drei Schritten. onConfirmed bekommt das neue „me“ (Sitzung bestätigt), aber erst nach Schritt 3,
 * damit die Codes nicht durch einen Seitenwechsel verschwinden.
 */
export function MfaSetupWizard({ email, onFinished, onCancel }: { email: string; onFinished: (me: Me) => void; onCancel?: () => void }) {
  const { t } = useTranslation();
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ codes: string[]; me: Me } | null>(null);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    let alive = true;
    startTotpSetup().then((s) => alive && setSetup(s), (e: unknown) => alive && setError(serverMessage(e) ?? t('errors.network')));
    return () => {
      alive = false;
    };
  }, []);

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await confirmTotp(code);
      setResult({ codes: r.recoveryCodes, me: r.me });
    } catch (err) {
      setError(serverMessage(err) ?? t('errors.network'));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="col" data-step="3">
        <div className="steps" aria-hidden><span className="done">1</span><span className="done">2</span><span className="on">3</span></div>
        <h3 style={{ margin: 0 }}>{t('account.codes.title')}</h3>
        <div className="muted small">{t('account.codes.text')}</div>
        <RecoveryCodes codes={result.codes} email={email} onDone={() => onFinished(result.me)} />
      </div>
    );
  }
  if (!setup) return error ? <div className="error" role="alert">{error}</div> : <Spinner />;
  return (
    <form className="col mfa-setup" onSubmit={confirm}>
      <div className="steps" aria-hidden><span className="on">1</span><span>2</span><span>3</span></div>
      <div className="mfa-qr-row">
        <QrImage qr={setup.qr} label={t('account.mfa.qrLabel')} />
        <div className="col" style={{ gap: 8 }}>
          <b>{t('account.mfa.step1')}</b>
          <div className="muted small">{t('account.mfa.step1Text')}</div>
          <button type="button" className="btn sm ghost" style={{ justifySelf: 'start' }} onClick={() => setShowKey((x) => !x)}><Icon name="key" />{t('account.mfa.manual')}</button>
          {showKey && <code className="mono key" data-testid="mfa-secret">{setup.secret}</code>}
        </div>
      </div>
      <b>{t('account.mfa.step2')}</b>
      <CodeInput value={code} onChange={setCode} label={t('account.mfa.code')} autoFocus={false} />
      <div className="error" role="alert">{error}</div>
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        {onCancel && <button type="button" className="btn ghost" onClick={onCancel}>{t('common.cancel')}</button>}
        <button className="btn primary" disabled={busy || code.length !== 6}>{t('account.mfa.confirm')}</button>
      </div>
    </form>
  );
}
